import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useHankoAuth from './hooks/useHankoAuth';

import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

import LoadingScreen from './components/LoadingScreen';
import AppLayout from './AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import PaidFeatureRoute from './components/subscription/PaidFeatureRoute';
import { AppFeature } from './components/subscription/AppFeatures';

// Login stays eager — it's the entry page for signed-out users.
import HankoLogin from './pages/LoginPage';
import { captureAttributionFromUrl } from './analytics/attribution';

// Route-level code splitting: each page loads its own chunk on first visit
// instead of shipping the whole app as one bundle.
const Home = lazy(() => import('./pages/Home'));
const Search = lazy(() => import('./pages/Search'));
const MyProfiles = lazy(() => import('./pages/MyProfiles'));
const MyBids = lazy(() => import('./pages/MyBids'));
const RegistrationPage = lazy(() => import('./pages/RegistrationPage'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const TenderView = lazy(() => import('./pages/TenderView'));
const Settings = lazy(() => import('./pages/Settings'));
const Ohjeet = lazy(() => import('./pages/Ohjeet'));
const DecisionSearch = lazy(() => import('./pages/DecisionSearch'));
const DecisionSearchHybrid = lazy(() => import('./pages/DecisionSearchHybrid'));
const DecisionsChatPage = lazy(() => import('./pages/DecisionsChatPage'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const BlockedAccess = lazy(() => import('./pages/BlockedAccess'));
const StatusHolding = lazy(() => import('./pages/StatusHolding'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const OAuthLoginPage = lazy(() => import('./pages/OAuthLoginPage'));
const EndingContractsTest = lazy(() => import('./pages/EndingContractsTest'));
const MarketOverview = lazy(() => import('./pages/MarketOverview'));
const IndustrySignalRadar = lazy(() => import('./pages/IndustrySignalRadar'));
const CompetitorAnalysis = lazy(() => import('./pages/CompetitorAnalysis'));
const BuyerDossier = lazy(() => import('./pages/BuyerDossier'));
const UpcomingRadar = lazy(() => import('./pages/UpcomingRadar'));
const MarketIntelHub = lazy(() => import('./pages/MarketIntelHub'));
const DailyBrief = lazy(() => import('./pages/DailyBrief'));
const BidLedger = lazy(() => import('./pages/BidLedger'));
const WeeklyNewsletter = lazy(() => import('./pages/WeeklyNewsletter'));

// Pro gets the deterministic Analytics views; Eye (Intelligence+) implies them.
const ANALYTICS_FEATURES = [AppFeature.MARKET_ANALYTICS, AppFeature.EYE_AI_CHAT];

const App = () => {
    const { isAuthenticated, authChecked, user } = useHankoAuth();
    // Internal experiment pages stay reachable for the team only.
    const isInternalUser = user?.email?.endsWith('@tendera.bid') ?? false;
    const location = useLocation();

    useEffect(() => {
        captureAttributionFromUrl(location);
    }, [location]);

    if (!authChecked || isAuthenticated === null) {
        return <LoadingScreen />;
    }

    return (
            <Suspense fallback={<LoadingScreen />}>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<HankoLogin />} />
                <Route path="/register" element={<RegistrationPage />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/blocked" element={<BlockedAccess />} />
                <Route path="/status-holding" element={<StatusHolding />} />
                {/* public endpoint for k8s health check */}
                <Route path="/health" element={<>OK</>} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                {/* Sign-in hop for connecting an app to MCP over OAuth; outside the onboarding gate like invites. */}
                <Route path="/oauth/login" element={<OAuthLoginPage />} />

                {/* Protected routes inside layout */}
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute>
                            <AppLayout>
                                {/* Inner boundary: keep the layout chrome visible while a page chunk loads */}
                                <Suspense fallback={<LoadingScreen />}>
                                <Routes>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/search" element={<Search />} />
                                    <Route path="/decisions" element={<DecisionSearch />} />
                                    <Route path="/decisions/chat" element={<DecisionsChatPage />} />
                                    <Route path="/decisions/:agentId" element={<DecisionSearch />} />
                                    {/* Internal search experiment - not linked anywhere, gated to the team */}
                                    {isInternalUser && <Route path="/decisions-hybrid" element={<DecisionSearchHybrid />} />}
                                    <Route path="/my-profiles" element={<MyProfiles />} />
                                    <Route path="/my-profiles/:profileId" element={<MyProfiles />} />
                                    <Route path="/ending-contracts" element={<EndingContractsTest />} />
                                    <Route path="/opportunities" element={<Opportunities />} />
                                    <Route path="/market-intel" element={
                                        <PaidFeatureRoute feature={ANALYTICS_FEATURES}>
                                            <MarketIntelHub />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/market-overview" element={
                                        <PaidFeatureRoute feature={ANALYTICS_FEATURES}>
                                            <MarketOverview />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/competitor-analysis" element={
                                        <PaidFeatureRoute feature={ANALYTICS_FEATURES}>
                                            <CompetitorAnalysis />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/buyer-dossier" element={
                                        <PaidFeatureRoute feature={ANALYTICS_FEATURES}>
                                            <BuyerDossier />
                                        </PaidFeatureRoute>
                                    } />
                                    {/* The radars are not part of Pro Analytics - Eye (Intelligence+) only */}
                                    <Route path="/signal-radar" element={
                                        <PaidFeatureRoute feature={AppFeature.EYE_AI_CHAT}>
                                            <IndustrySignalRadar />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/upcoming-radar" element={
                                        <PaidFeatureRoute feature={AppFeature.EYE_AI_CHAT}>
                                            <UpcomingRadar />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/daily-brief" element={
                                        <PaidFeatureRoute feature={AppFeature.DAILY_BRIEF}>
                                            <DailyBrief />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/bid-ledger" element={
                                        <PaidFeatureRoute feature={AppFeature.BID_LEDGER}>
                                            <BidLedger />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/weekly-newsletter" element={
                                        <PaidFeatureRoute feature={AppFeature.MARKET_DATA_NEWSLETTER}>
                                            <WeeklyNewsletter />
                                        </PaidFeatureRoute>
                                    } />
                                    <Route path="/mybids/:tabSlug?" element={<MyBids />} />
                                    <Route path="/tender/:id" element={<TenderView />} />
                                    <Route path="/settings/*" element={<Settings />} />
                                    <Route path="/ohjeet" element={<Ohjeet />} />
                                    {/* Legacy help route → redirect to the new global help hub */}
                                    <Route path="/help" element={<Navigate to="/ohjeet" replace />} />
                                </Routes>
                                </Suspense>
                            </AppLayout>
                        </ProtectedRoute>
                    }
                />
            </Routes>
            </Suspense>
    );
};

export default App;
